import React, { useState } from 'react';
import {
  Typography,
  Box,
  Button,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { formatBedroomBathroom, hasBedroomAndBathroomValues } from '../../utils/formatters';

const RoomManagement = ({ rooms = [], onRoomsChange, propertyType }) => {
  const [openRoomDialog, setOpenRoomDialog] = useState(false);
  const [editIndex, setEditIndex] = useState(null); // 新增：编辑索引
  const [newRoom, setNewRoom] = useState({
    roomNumber: '',
    squareFeet: '',
    price: '',
    hasBathroom: false,
    availableDate: '',
    bedrooms: '',
    bathrooms: '',
  });
  const [errors, setErrors] = useState({}); // 新增：错误提示

  // 确保rooms是数组
  const safeRooms = Array.isArray(rooms) ? rooms : [];

  const handleDeleteRoom = (roomIndex) => {
    const updatedRooms = safeRooms.filter((_, i) => i !== roomIndex);
    onRoomsChange(updatedRooms);
  };

  const validate = () => {
    const err = {};
    if (!newRoom.roomNumber) err.roomNumber = 'Required';
    if (!newRoom.squareFeet) err.squareFeet = 'Required';
    if (!newRoom.price) err.price = 'Required';
    if (!newRoom.availableDate) err.availableDate = 'Required';
    if (propertyType === 'Apartment') {
      if (newRoom.bedrooms === '' || newRoom.bedrooms === null || newRoom.bedrooms === undefined) {
        err.bedrooms = 'Required';
      }
      if (newRoom.bathrooms === '' || newRoom.bathrooms === null || newRoom.bathrooms === undefined) {
        err.bathrooms = 'Required';
      }
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleOpenAdd = () => {
    setEditIndex(null);
    setNewRoom({
      roomNumber: '',
      squareFeet: '',
      price: '',
      hasBathroom: false,
      availableDate: '',
      bedrooms: '',
      bathrooms: '',
    });
    setErrors({});
    setOpenRoomDialog(true);
  };

  const handleOpenEdit = (room, idx) => {
    setEditIndex(idx);
    setNewRoom({ ...room });
    setErrors({});
    setOpenRoomDialog(true);
  };

  const handleSave = () => {
    if (!validate()) return;
    if (editIndex !== null) {
      // 编辑
      const updated = [...safeRooms];
      updated[editIndex] = { ...newRoom };
      onRoomsChange(updated);
    } else {
      // 新增
      onRoomsChange([...safeRooms, { ...newRoom }]);
    }
    setOpenRoomDialog(false);
  };

  return (
    <>
      <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3 }}>
        Room Management
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleOpenAdd}
        >
          Add New Room
        </Button>
      </Box>

      {/* Rooms List */}
      {safeRooms.map((room, index) => (
        <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Room #{room.roomNumber}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Square Feet: {room.squareFeet}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Price: ${room.price}/month
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Bathroom: {room.hasBathroom ? 'Private' : 'Shared'}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Available From: {room.availableDate}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                {hasBedroomAndBathroomValues(room.bedrooms, room.bathrooms)
                  ? formatBedroomBathroom(room.bedrooms, room.bathrooms)
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                color="primary"
                onClick={() => handleOpenEdit(room, index)}
              >
                Edit
              </Button>
              <Button
                color="error"
                onClick={() => handleDeleteRoom(index)}
              >
                Delete Room
              </Button>
            </Grid>
          </Grid>
        </Paper>
      ))}

      {/* Add/Edit Room Dialog */}
      <Dialog open={openRoomDialog} onClose={() => setOpenRoomDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editIndex !== null ? 'Edit Room' : 'Add New Room'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Room Number"
                value={newRoom.roomNumber}
                onChange={(e) => setNewRoom(prev => ({ ...prev, roomNumber: e.target.value }))}
                required
                error={!!errors.roomNumber}
                helperText={errors.roomNumber && <span style={{ color: 'red' }}>{errors.roomNumber}</span>}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Square Feet"
                type="number"
                value={newRoom.squareFeet}
                onChange={(e) => setNewRoom(prev => ({ ...prev, squareFeet: e.target.value }))}
                required
                error={!!errors.squareFeet}
                helperText={errors.squareFeet && <span style={{ color: 'red' }}>{errors.squareFeet}</span>}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Monthly Rent"
                type="number"
                value={newRoom.price}
                onChange={(e) => setNewRoom(prev => ({ ...prev, price: e.target.value }))}
                required
                error={!!errors.price}
                helperText={errors.price && <span style={{ color: 'red' }}>{errors.price}</span>}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newRoom.hasBathroom}
                    onChange={(e) => setNewRoom(prev => ({ ...prev, hasBathroom: e.target.checked }))}
                  />
                }
                label="Private Bathroom"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Available Date"
                type="date"
                value={newRoom.availableDate}
                onChange={(e) => setNewRoom(prev => ({ ...prev, availableDate: e.target.value }))}
                required
                InputLabelProps={{ shrink: true }}
                error={!!errors.availableDate}
                helperText={errors.availableDate && <span style={{ color: 'red' }}>{errors.availableDate}</span>}
              />
            </Grid>
            {/* 只在Apartment时渲染bedrooms/bathrooms输入框 */}
            {propertyType === 'Apartment' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Total Bedrooms"
                    type="number"
                    value={newRoom.bedrooms}
                    onChange={(e) => setNewRoom(prev => ({ ...prev, bedrooms: e.target.value }))}
                    required
                    error={!!errors.bedrooms}
                    helperText={
                      <span>
                        {errors.bedrooms ? (
                          <span style={{ color: 'red', display: 'block' }}>{errors.bedrooms}</span>
                        ) : null}
                        <span style={{ opacity: 0.7, fontSize: '0.75rem' }}>If it's a studio, please enter 0 bedrooms 1 bathroom.</span>
                      </span>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Total Bathrooms"
                    type="number"
                    inputProps={{ step: 0.5 }}
                    value={newRoom.bathrooms}
                    onChange={(e) => setNewRoom(prev => ({ ...prev, bathrooms: e.target.value }))}
                    required
                    error={!!errors.bathrooms}
                    helperText={errors.bathrooms && <span style={{ color: 'red' }}>{errors.bathrooms}</span>}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRoomDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
          >
            {editIndex !== null ? 'Save' : 'Add Room'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RoomManagement; 